// A sensor's history: range buttons (24 h / 7 days / 30 days), the temperature chart (Recharts, the
// library the beacon battery chart uses) with the limits as dashed lines and alerts as thin marks,
// then the alert history (time, the text, who was texted / pushed). Shared by the desktop /sensors
// page and the phone screen; reads through features/sensors/useSensors.js.

import React, { useMemo, useState } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useSensorReadings } from '../../features/sensors/useSensors';
import {
  SENSOR_RANGES,
  alertDelivery,
  alertKind,
  alertMarks,
  chartRows,
  dayTicks,
  fmtLimit,
  fmtTemp,
  limitsInView,
  num,
  resolutionText,
  tickText,
  yDomain,
} from '../../features/sensors/sensorRules';
import { shopClock } from '../../utils/shopDate';
import { SENSOR_TONE } from './SensorCard';

const LINE_COLOR = '#0d9488';
const MARK_COLOR = {
  hot: SENSOR_TONE.hot.border,
  cold: SENSOR_TONE.cold.border,
  offline: SENSOR_TONE.offline.border,
  probe: SENSOR_TONE.probe.border,
  ok: '#16a34a',
  muted: 'var(--bs-secondary-color)',
};

const whenText = (ms) => new Date(ms).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago',
}).replace(/[  ]/g, ' ');

function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  if (!p || p.gap) return null;
  return (
    <div className="border rounded px-2 py-1 small shadow-sm" style={{ background: 'var(--bs-body-bg)', color: 'var(--bs-body-color)' }}>
      <div className="fw-bold">{p.f === null ? 'No temperature' : fmtTemp(p.f)}</div>
      {p.band && <div className="text-muted">low {fmtTemp(p.band[0])} · high {fmtTemp(p.band[1])}</div>}
      <div className="text-muted">{whenText(p.t)}</div>
    </div>
  );
}

/** The chart alone. `data` = GET /sensors/{id}/readings; `hours` = the range asked for. */
export function SensorChart({ data, hours, height = 260 }) {
  const rows = useMemo(() => chartRows(data), [data]);
  const sensor = (data && data.sensor) || {};
  const low = num(sensor.low_f);
  const high = num(sensor.high_f);
  // The window ends when this answer arrived (fixed per answer, so the axis doesn't creep each render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const end = useMemo(() => Date.now(), [data]);
  const start = end - hours * 3600 * 1000;
  const marks = useMemo(() => alertMarks(data && data.alerts, start, end), [data, start, end]);
  const ticks = useMemo(() => dayTicks(start, end, hours), [start, end, hours]);
  const bucketed = (num(data && data.bucket_seconds) || 0) > 0;
  const hasReading = rows.some((r) => r.f !== null);
  if (!hasReading) {
    return (
      <div className="text-muted small border rounded p-3 text-center" data-testid="sensor-chart-empty">
        No temperature readings in this range yet.
      </div>
    );
  }
  const [y0, y1] = yDomain(rows, low, high);
  const inView = limitsInView(rows, low, high);
  const offChart = [
    low !== null && !inView.low ? `low limit ${fmtLimit(low)} is below the chart` : null,
    high !== null && !inView.high ? `high limit ${fmtLimit(high)} is above the chart` : null,
  ].filter(Boolean);
  return (
    <div data-testid="sensor-chart">
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 10, right: 12, bottom: 0, left: -6 }}>
            <CartesianGrid stroke="var(--bs-border-color)" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="t" type="number" scale="time" domain={[start, end]} ticks={ticks || undefined} tickFormatter={(v) => tickText(v, hours)}
              tick={{ fontSize: 11, fill: 'var(--bs-secondary-color)' }} stroke="var(--bs-border-color)" minTickGap={40} />
            <YAxis domain={[y0, y1]} tickFormatter={(v) => `${v}°`} allowDecimals={false}
              tick={{ fontSize: 11, fill: 'var(--bs-secondary-color)' }} stroke="var(--bs-border-color)" width={42} />
            {bucketed && (
              <Area dataKey="band" stroke="none" fill={LINE_COLOR} fillOpacity={0.15} isAnimationActive={false}
                connectNulls={false} activeDot={false} />
            )}
            {inView.high && (
              <ReferenceLine y={high} stroke={SENSOR_TONE.hot.border} strokeDasharray="6 4" ifOverflow="extendDomain"
                label={{ value: `high ${fmtLimit(high)}`, position: 'insideTopRight', fontSize: 11, fill: SENSOR_TONE.hot.border }} />
            )}
            {inView.low && (
              <ReferenceLine y={low} stroke={SENSOR_TONE.cold.border} strokeDasharray="6 4" ifOverflow="extendDomain"
                label={{ value: `low ${fmtLimit(low)}`, position: 'insideBottomRight', fontSize: 11, fill: SENSOR_TONE.cold.border }} />
            )}
            {marks.map((m, i) => (
              <ReferenceLine key={`a${i}`} x={m.t} stroke={MARK_COLOR[m.tone] || MARK_COLOR.muted}
                strokeOpacity={m.problem ? 0.9 : 0.55} strokeDasharray={m.problem ? undefined : '2 3'} ifOverflow="hidden" />
            ))}
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'var(--bs-secondary-color)', strokeDasharray: '3 3' }} />
            <Line type="monotone" dataKey="f" stroke={LINE_COLOR} strokeWidth={2} dot={false} connectNulls={false}
              activeDot={{ r: 4, stroke: 'var(--bs-body-bg)', strokeWidth: 2 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="d-flex flex-wrap gap-3 small text-muted mt-1">
        <span><span style={{ display: 'inline-block', width: 14, height: 2, background: LINE_COLOR, verticalAlign: 'middle' }} /> °F ({resolutionText(data && data.bucket_seconds)})</span>
        {(inView.low || inView.high) && <span>- - - alert limits</span>}
        {offChart.map((t) => <span key={t} data-testid="sensor-limit-off-chart">{t}</span>)}
        {marks.length > 0 && <span>│ alerts (listed below)</span>}
      </div>
    </div>
  );
}

/** The alert history, newest first. */
export function SensorAlertList({ alerts, compact = false }) {
  const list = Array.isArray(alerts) ? alerts : [];
  if (!list.length) return <div className="small text-muted" data-testid="sensor-alerts-empty">No alerts in this range.</div>;
  return (
    <ul className="list-unstyled mb-0" data-testid="sensor-alerts">
      {list.map((a) => {
        const k = alertKind(a.kind);
        const d = alertDelivery(a);
        return (
          <li key={a.alert_id} className="py-2" style={{ borderTop: '1px solid var(--bs-border-color)' }} data-testid={`sensor-alert-${a.alert_id}`}>
            <div className="d-flex flex-wrap align-items-baseline gap-2">
              <strong className="text-nowrap">{shopClock(a.created_at)}</strong>
              <span style={{ color: MARK_COLOR[k.tone] || MARK_COLOR.muted, fontWeight: 600, fontSize: 13 }}>{k.label}</span>
            </div>
            <div style={{ fontSize: compact ? 13 : 14 }}>{a.text}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>{d.sent}</div>
            {d.errors.map((e) => (
              <div key={e} style={{ fontSize: 12, color: SENSOR_TONE.offline.fg }}>{e}</div>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

/** Range buttons + chart + window stats + alert list for one sensor. `stamp` re-reads it. */
export default function SensorHistory({ sensor, stamp, chartHeight = 260, compact = false, reloadRef }) {
  const [rangeKey, setRangeKey] = useState('24h');
  const range = SENSOR_RANGES.find((r) => r.key === rangeKey) || SENSOR_RANGES[0];
  const { data, error, loading, reload } = useSensorReadings(sensor ? sensor.sensor_id : null, range.hours, stamp);
  if (reloadRef) reloadRef.current = reload;
  if (!sensor) return null;
  const stats = data && data.stats;
  return (
    <div data-testid="sensor-history">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <strong>{sensor.name}</strong>
        <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="Chart range">
          {SENSOR_RANGES.map((r) => (
            <button key={r.key} type="button" className={`btn ${r.key === rangeKey ? 'btn-secondary' : 'btn-outline-secondary'}`}
              aria-pressed={r.key === rangeKey} onClick={() => setRangeKey(r.key)}>{r.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="small text-danger mb-2" role="alert">{error}</div>}
      {loading && <div className="small text-muted">Loading readings…</div>}
      {data && (
        <>
          <SensorChart data={data} hours={range.hours} height={chartHeight} />
          {stats && num(stats.readings) > 0 && (
            <div className="small text-muted mt-1" data-testid="sensor-range-stats">
              {range.label}: low {fmtTemp(stats.min_f)} · high {fmtTemp(stats.max_f)} · average {fmtTemp(stats.avg_f)} · {stats.readings} readings
            </div>
          )}
          <h6 className="mt-3 mb-1">Alerts ({range.label})</h6>
          <SensorAlertList alerts={data.alerts} compact={compact} />
        </>
      )}
    </div>
  );
}
